import Backbone from 'backbone';

import {   
  getHomeSubpage,
  getSubpageBySlugOrId,
  getSubpageForContent,
  getContentItemByUuid,
  getAssetForContent,
  getSubpageForSection,
  getSectionForContent,
  getSectionById,
  getSelectedSubmenuItem,
  getMenuItemById,
  getCurrentSubpage
} from 'zoomforth_codesbane_viewer/reducers/index.js';
import { getSubpageSlug } from 'zoomforth_codesbane_viewer/lib/subpages.js';

import {
  setCurrentSubpageId,
  setOverlayContentUuid,
  setLastClickedSubmenuItemId,
  setLastClickedToplevelInternalLinkId,
  goToContent,
  scrollToSection,
  setScrollToTop
} from 'zoomforth_codesbane_viewer/action_creators/viewer.js';

import {
  getContentItemType
} from 'zoomforth_codesbane_viewer/lib/page_content_utils.js';


const isAccessibleSubpage = (subpage)=>
  !!(subpage && !subpage.isHidden);


const Router = Backbone.Router.extend({

  routes: {

    //// Modern URLs
    
    // these URLs are ingress points that cause state change.
    // the URL should not change automatically after these ingress points -- 
    
    // if a user enters a page using one of these URLs, that URL does not change until they
    // do an action that prompts that change -- either going to a different URL, 
    // or sometimes a URL change as a silent event, 
    // to better reflect the current state for if the user shares the page.
    // (e.g. closed overlay, went to next item in the gallery)
    
    '': 'handleHome',

    // e.g., #/subpage/4k50j
    'subpage/:subpageSlug': 'handleSubpage',

    // e.g., #/subpage/4k50j/section/873gh
    // scroll to section
    'subpage/:subpageSlug/section/:sectionId': 'handleSectionWithinSubpage',
    
    // scroll to content (playing it if it is a playlist/slideshow), 
    'subpage/:subpageSlug/content/:contentUuid': 'handleContentWithinSubpage',
    
    // open overlay(and play it if playable)
    'subpage/:subpageSlug/overlay/:contentUuid': 'handleOverlayContent',

    // initiate download
    'subpage/:subpageSlug/download/:contentUuid': 'handleDownloadableContent',

    //// Legacy support
    'home': 'handleLegacyHome', 
    'overlay/:contentUuid': 'handleLegacyOverlay',
    'overlay/:contentUuid/play': 'handleLegacyOverlay',
    'section/:sectionId': 'handleLegacySection',
    'section/:sectionId/scroll/top': 'handleLegacySectionScrollToTop',
    'section/:sectionId/scroll/:menuItemId': 'handleLegacySectionScrollToMenuItemId',
    'section/:sectionId/:contentUuid': 'handleLegacyShowContent',
    'section/:sectionId/:contentUuid/play': 'handleLegacyShowContent'
  },

  
  initialize(options) {
    this.store = options.store;
  },

  //// Modern Handlers

  handleHome() {
    const homeSubpage = getHomeSubpage(this.store.getState());
    if (!isAccessibleSubpage(homeSubpage)) {
      return; // fail silently so we don't break pages with crazy data
    }
    this.handleSubpage(homeSubpage.id);
  },

  handleSubpage(subpageSlugOrId) {
    const subpage = getSubpageBySlugOrId(this.store.getState(), subpageSlugOrId);
    const currentSubpageInState = getCurrentSubpage(this.store.getState());

    this.store.dispatch(setScrollToTop());

    // subpageSlugOrId is an ID but the subpage has a slug
    if(subpage && subpage.slug && subpage.slug !== subpageSlugOrId){
      if (currentSubpageInState && subpage.id !== currentSubpageInState.id) {
        // redirect to the slug
        this.navigateToSubpage(subpage, { trigger:false, replace:true });
      }
    }

    // reset the selected content item
    this.store.dispatch(setLastClickedSubmenuItemId(null));
    this.store.dispatch(setLastClickedToplevelInternalLinkId(null));

    if (!isAccessibleSubpage(subpage)) {
      this.navigateHome({ trigger:true, replace:true });
      return;
    }

    this.store.dispatch(setCurrentSubpageId(subpage.id));
  },

  /*
    
    Perform page state updates to scroll to the content specified, within the subpage specified
    
  */
  handleSectionWithinSubpage(subpageSlugOrId, sectionId) {
    const state = this.store.getState();
    const subpage = getSubpageBySlugOrId(state, subpageSlugOrId); // This will still fetch inactive subpages by ID but that is ok
    if (!isAccessibleSubpage(subpage)) {
      this.navigateHome({ trigger:true, replace:true });
      return;
    }

    // subpageSlugOrId is an ID but the subpage has a slug
    if(subpage && subpage.slug && subpage.slug !== subpageSlugOrId){
      // redirect to the slug URL
      this.navigateToSubpageAndSection(subpage, sectionId, { trigger:false, replace:true });
    }

    const section = getSectionById(state, sectionId);
    if (!section) {
      this.navigateToSubpage(subpage, { trigger:true, replace:true });
      return;
    }
    this.store.dispatch(setCurrentSubpageId(subpage.id));
    this.store.dispatch(scrollToSection(section.id, { animationTimeMs: 0 }));

    const selectedSubmenuItem = getSelectedSubmenuItem(state, subpage, null, section);
    if (selectedSubmenuItem) {
      this.store.dispatch(setLastClickedSubmenuItemId(selectedSubmenuItem.id));
    }
  },
  

  /*

    Perform page state updates to "go" to the content selected
  
    in all cases, this will mean scrolling to the element, and in some cases, this will mean playing it
    (namely, playing the content if it is a playlist/slideshow).

    We do not open the overlay—ever—from here!
  
    if we notice an invalid input we give up and let the page state just stay at the subpage level
  */
  handleContentWithinSubpage(subpageSlugOrId, contentUuid) {
    const state = this.store.getState();

    // Validation of inputs
    const subpage = getSubpageBySlugOrId(state, subpageSlugOrId);
    if (!isAccessibleSubpage(subpage)) {
      this.navigateHome({ trigger:true, replace:true });
      return;
    }

    // subpageSlugOrId is an ID but the subpage has a slug
    if(subpage && subpage.slug && subpage.slug !== subpageSlugOrId){
      // redirect to the slug URL
      this.navigateToSubpageAndContent(subpage, contentUuid, { trigger:false, replace:true });
    }

    const content = getContentItemByUuid(state, contentUuid);
    if (!content) {
      // if there is no content, there is no way of knowing which section the content would belong to, 
      // so we have to fall back to only showing the subpage instead of going to the content's section
      this.navigateToSubpage(subpage, { trigger:true, replace:true });
      return;
    }
    
    // Validate that content is actually contained in section
    const section = getSectionForContent(state, contentUuid);
    if (!section) {
      this.navigateToSubpage(subpage, { trigger:true, replace:true });
      return;
    }

    const subpageFromContent = getSubpageForContent(state, content);
    if (!subpageFromContent || subpageFromContent.id != subpage.id) {
      // this mismatch should never occur in normal data -- do we want to raise an error here,
      // or let this case pass silently? passing silently for now.
      // maybe we want to require that "goToContent" be passed a subpage, 
      // and move this check into there and throw a real error?
      this.navigateToSubpage(subpage, { trigger:true, replace:true });
      return;
    }

    this.store.dispatch(setCurrentSubpageId(subpage.id));
    this.store.dispatch(goToContent(content, { animationTimeMs: 0 }));

    const selectedSubmenuItem = getSelectedSubmenuItem(state, subpage, content, section);
    if (selectedSubmenuItem) {
      this.store.dispatch(setLastClickedSubmenuItemId(selectedSubmenuItem.id));
    }
  },
  
  handleOverlayContent(subpageSlugOrId, contentUuid) {
    const state = this.store.getState();
    const subpage = getSubpageBySlugOrId(state, subpageSlugOrId);

    if (!isAccessibleSubpage(subpage)) {
      this.navigateHome({ trigger:true, replace:true });
      return;
    }

    // subpageSlugOrId is an ID but the subpage has a slug
    if(subpage && subpage.slug && subpage.slug !== subpageSlugOrId){
      // redirect to the slug URL
      this.navigateToOverlay(subpage, contentUuid, { trigger:false, replace:true });
    }
    
    const content = getContentItemByUuid(state, contentUuid);
        
    if (!content || getContentItemType(content) === 'text') {
      // if there is no content, there is no way of knowing which section the content would belong to, 
      // so we have to fall back to only showing the subpage instead of going to the content's section
      this.navigateToSubpage(subpage, { trigger:true, replace:true });
      return;
    }

    const hasSectionForContent = getSectionForContent(state, content);

    this.store.dispatch(setCurrentSubpageId(subpage.id));

    if (hasSectionForContent) {
      this.store.dispatch(goToContent(content, { animationTimeMs: 0 }));
    }

    this.store.dispatch(setOverlayContentUuid(content.uuid));
  },

  handleDownloadableContent(subpageSlugOrId, contentUuid) {
    const state = this.store.getState();
    const subpage = getSubpageBySlugOrId(state, subpageSlugOrId);

    if (!isAccessibleSubpage(subpage)) {
      this.navigateHome({ trigger:true, replace:true });
      return;
    }

    const content = getContentItemByUuid(state, contentUuid);
    const asset = getAssetForContent(state, content);

    if (!content || !asset || !asset.link) {
      this.navigateHome({ trigger:true, replace:true });
      return;
    }

    window.open(asset.link, '_self');

    // to allow this to be clicked again we update the URL
    this.navigateToSubpageAndContent(subpage, contentUuid, { trigger: false, replace: true });
  },

  //// Navigation shortcut functions

  navigateHome({ trigger, replace }) {
    this.navigate('', { trigger, replace });
  },

  navigateToSubpage(subpage, { trigger, replace }) {
    this.navigate(`/subpage/${getSubpageSlug(subpage)}`, { trigger, replace });
  },

  // This is never used?
  navigateToSubpageAndSection(subpage, sectionId, { trigger, replace }) {
    this.navigate(`/subpage/${getSubpageSlug(subpage)}/section/${sectionId}`, { trigger, replace });
  },

  navigateToSubpageAndContent(subpage, contentUuid, { trigger, replace }) {
    this.navigate(
      `/subpage/${getSubpageSlug(subpage)}/content/${contentUuid}`, 
      { trigger, replace }
    );
  },
  
  navigateToOverlay(subpage, contentUuid, { trigger, replace }) {
    this.navigate(
      `/subpage/${getSubpageSlug(subpage)}/overlay/${contentUuid}`, 
      { trigger, replace }
    );
  },


  //// Legacy URLS Handlers

  handleLegacyHome() {
    this.navigateHome({ trigger:true, replace:true });
  },

  handleLegacyOverlay(contentUuid) {
    const state = this.store.getState();
    const subpage = getSubpageForContent(state, contentUuid);
    const contentItem = getContentItemByUuid(state, contentUuid);
    if (!isAccessibleSubpage(subpage)) {
      this.navigateHome({ trigger:true, replace:true });
      return;
    }
    if (!contentItem) {
      this.navigateToSubpage(subpage, { trigger:true, replace:true });
      return;
    }
    this.navigateToOverlay(
      subpage, contentItem.uuid, { trigger:true, replace:true }
    );
  },

  handleLegacySection(sectionId) {
    const subpage = getSubpageForSection(this.store.getState(), sectionId);
    if (!isAccessibleSubpage(subpage)) {
      this.navigateHome({ trigger:true, replace: true });
      return;
    }
    this.navigateToSubpage(subpage, { trigger:true, replace: true });
  },

  handleLegacySectionScrollToTop(sectionId) {
    // We can go directly to the link here and not worry about scroll for legacy
    const subpage = getSubpageForSection(this.store.getState(), sectionId);
    if (!isAccessibleSubpage(subpage)) {
      this.navigateHome({ trigger:true, replace: true });
      return;
    }
    this.navigateToSubpage(subpage, { trigger: true, replace:true });
  },

  handleLegacySectionScrollToMenuItemId(sectionId, menuItemId) {
    // We can go directly to the anchored content item and not worry about scroll for legacy
    const subpage = getSubpageForSection(this.store.getState(), sectionId);
    const menuItem = getMenuItemById(this.store.getState(), menuItemId);

    this.navigateToSubpageAndContent(subpage, menuItem.appAction.contentUuid, {trigger: true, replace: true});
  },

  handleLegacyShowContent(sectionId, contentUuid) {
    const subpage = getSubpageForSection(this.store.getState(), sectionId);
    if (!isAccessibleSubpage(subpage)) {
      this.navigateHome({ trigger:true, replace: true });
      return;
    }
    this.navigateToSubpageAndContent(subpage, contentUuid, {trigger: true, replace: true});
  }
});
    
  
export default Router;
